/**
 * QuestionsStep.tsx
 *
 * Build Bar -- Step 1: Preguntas.
 *
 * Shows AI-generated questions one at a time (like Claude Code's /init),
 * then a review screen before advancing to the Plan step.
 *
 * Flow:
 *   Loading -> Question 1 -> Question 2 -> ... -> Review -> Plan
 *
 * Every question type (yes_no, multiple_choice, multi_select, short_text)
 * also renders an optional free-text input so the user can always elaborate
 * in their own words.
 *
 * Layout (NO page scroll -- fits the available viewport height):
 *
 *   +-- .container (flex column, height: 100%) -------------------------+
 *   |  +-- .header (progress + question counter) ---------------------+ |
 *   |  +-- .body (flex: 1, centered question or review) --------------+ |
 *   |  +-- .footer (navigation buttons) ------------------------------+ |
 *   +-------------------------------------------------------------------+
 */

import { useState, useEffect, useCallback, useRef } from 'react';

import toast from 'react-hot-toast';

import { Skeleton, Button } from '@/components/ui';
import { extractApiError } from '@/lib/axios';
import { useWizardStore } from '@/stores/useWizardStore';
import { useGenerateQuestions, useSaveQuestionAnswers } from '@/api/useWizardV2';
import type { WizardQuestion } from '@/types/wizardV2';
import styles from './QuestionsStep.module.css';

// ---- Helpers ----------------------------------------------------------------

/**
 * Parse a compound answer string back into its parts.
 * Format: "Option A | Option B -- free text here"
 */
const parseAnswer = (raw: string): { selected: string[]; freeText: string } => {
  if (!raw) return { selected: [], freeText: '' };
  const dashIdx = raw.indexOf(' — ');
  const optionsPart = dashIdx >= 0 ? raw.slice(0, dashIdx) : raw;
  const freeText = dashIdx >= 0 ? raw.slice(dashIdx + 3) : '';
  const selected = optionsPart
    .split(' | ')
    .map((s) => s.trim())
    .filter(Boolean);
  return { selected, freeText };
};

/**
 * Build a compound answer string from parts.
 */
const buildAnswer = (selected: string[], freeText: string): string => {
  const parts: string[] = [];
  if (selected.length > 0) parts.push(selected.join(' | '));
  if (freeText.trim()) {
    if (parts.length > 0) parts.push(' — ' + freeText.trim());
    else parts.push(freeText.trim());
  }
  return parts.join('');
};

// ---- Sub-component: SingleQuestion -----------------------------------------

interface SingleQuestionProps {
  question: WizardQuestion;
  answer: string;
  onAnswer: (id: string, answer: string) => void;
  index: number;
  total: number;
}

const SingleQuestion = ({ question, answer, onAnswer, index, total }: SingleQuestionProps) => {
  const { selected, freeText } = parseAnswer(answer);
  const textRef = useRef<HTMLInputElement>(null);

  const updateAnswer = (newSelected: string[], newFreeText: string) => {
    onAnswer(question.id, buildAnswer(newSelected, newFreeText));
  };

  const handlePillClick = (option: string) => {
    if (question.type === 'multi_select') {
      const next = selected.includes(option)
        ? selected.filter((s) => s !== option)
        : [...selected, option];
      updateAnswer(next, freeText);
    } else {
      const next = selected[0] === option ? [] : [option];
      updateAnswer(next, freeText);
    }
  };

  const handleFreeTextChange = (value: string) => {
    updateAnswer(selected, value);
  };

  const hasOptions = question.type !== 'short_text';
  const options =
    question.type === 'yes_no'
      ? ['Yes', 'No']
      : question.options ?? [];

  return (
    <div className={styles.questionContainer}>
      <span className={styles.questionCounter}>
        {index + 1} / {total}
      </span>

      <div className={styles.progressTrack}>
        <div
          className={styles.progressFill}
          style={{ width: `${((index + 1) / total) * 100}%` }}
        />
      </div>

      <h2 className={styles.questionText}>{question.text}</h2>

      {question.type === 'multi_select' && (
        <p className={styles.multiSelectHint}>You can select multiple options</p>
      )}

      {hasOptions && options.length > 0 && (
        <div className={styles.optionsGroup}>
          {options.map((option) => (
            <button
              key={option}
              type="button"
              className={[
                styles.optionPill,
                selected.includes(option) ? styles.optionPillSelected : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => handlePillClick(option)}
              aria-pressed={selected.includes(option)}
            >
              {option}
            </button>
          ))}
        </div>
      )}

      <div className={styles.freeTextRow}>
        {hasOptions && (
          <span className={styles.freeTextLabel}>Or write your answer:</span>
        )}
        <input
          ref={textRef}
          type="text"
          className={styles.freeTextInput}
          value={question.type === 'short_text' ? (selected[0] || freeText) : freeText}
          onChange={(e) => {
            if (question.type === 'short_text') {
              onAnswer(question.id, e.target.value);
            } else {
              handleFreeTextChange(e.target.value);
            }
          }}
          placeholder={
            question.type === 'short_text'
              ? 'Write your answer...'
              : 'Add additional context...'
          }
          aria-label={question.text}
        />
      </div>
    </div>
  );
};

// ---- Sub-component: ReviewScreen -------------------------------------------

interface ReviewScreenProps {
  questions: WizardQuestion[];
  answers: Record<string, string>;
  onEdit: (index: number) => void;
}

const ReviewScreen = ({ questions, answers, onEdit }: ReviewScreenProps) => {
  return (
    <div className={styles.reviewContainer}>
      <h2 className={styles.reviewTitle}>Review your answers</h2>
      <p className={styles.reviewSubtitle}>
        You can edit any answer before continuing.
      </p>

      <div className={styles.reviewList}>
        {questions.map((q, idx) => {
          const answer = answers[q.id] || '';
          return (
            <div key={q.id} className={styles.reviewItem}>
              <div className={styles.reviewQuestion}>
                <span className={styles.reviewNumber}>{idx + 1}.</span>
                {q.text}
              </div>
              <div className={styles.reviewAnswerRow}>
                <span className={answer ? styles.reviewAnswer : styles.reviewSkipped}>
                  {answer || 'No answer'}
                </span>
                <button
                  type="button"
                  className={styles.reviewEditBtn}
                  onClick={() => onEdit(idx)}
                >
                  Edit
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---- Main component --------------------------------------------------------

export const QuestionsStep = () => {
  const {
    presentationId,
    questions,
    questionAnswers,
    currentQuestionIndex,
    isQuestionsLoading,
    setQuestions,
    setQuestionAnswer,
    setCurrentQuestionIndex,
    setIsQuestionsLoading,
    goToBuildStep,
  } = useWizardStore();

  const [showReview, setShowReview] = useState(false);

  const generateQuestions = useGenerateQuestions();
  const saveAnswers = useSaveQuestionAnswers();

  // ---- Generate questions on mount ------------------------------------------

  useEffect(() => {
    if (!presentationId || questions.length > 0 || isQuestionsLoading || generateQuestions.isPending) {
      return;
    }

    setIsQuestionsLoading(true);
    generateQuestions.mutate(
      { id: presentationId },
      {
        onSuccess: (result: WizardQuestion[]) => {
          setQuestions(result);
          setIsQuestionsLoading(false);
        },
        onError: (err) => {
          setIsQuestionsLoading(false);
          toast.error(extractApiError(err, 'Could not generate questions.'), {
            duration: 8_000,
          });
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentationId]);

  // ---- Handlers -------------------------------------------------------------

  const handleAnswer = useCallback(
    (id: string, answer: string) => {
      setQuestionAnswer(id, answer);
    },
    [setQuestionAnswer],
  );

  const handleNext = useCallback(() => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setShowReview(true);
    }
  }, [currentQuestionIndex, questions.length, setCurrentQuestionIndex]);

  const handleBack = useCallback(() => {
    if (showReview) {
      setShowReview(false);
    } else if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  }, [showReview, currentQuestionIndex, setCurrentQuestionIndex]);

  const handleEditFromReview = useCallback(
    (index: number) => {
      setShowReview(false);
      setCurrentQuestionIndex(index);
    },
    [setCurrentQuestionIndex],
  );

  const handleFinish = useCallback(async () => {
    if (!presentationId) {
      goToBuildStep(2);
      return;
    }

    const answers = Object.entries(questionAnswers)
      .filter(([, v]) => v.trim() !== '')
      .map(([id, answer]) => ({ id, answer }));

    if (answers.length === 0) {
      goToBuildStep(2);
      return;
    }

    try {
      await saveAnswers.mutateAsync({ id: presentationId, answers });
    } catch (err) {
      toast.error(extractApiError(err, 'Could not save answers, continuing.'), {
        duration: 4_000,
      });
    }

    goToBuildStep(2);
  }, [presentationId, questionAnswers, saveAnswers, goToBuildStep]);

  const handleSkip = useCallback(() => {
    goToBuildStep(2);
  }, [goToBuildStep]);

  // ---- Derived state --------------------------------------------------------

  const isLoading = isQuestionsLoading || generateQuestions.isPending;
  const isSaving = saveAnswers.isPending;
  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex >= questions.length - 1;

  // ---- Render ---------------------------------------------------------------

  return (
    <div className={styles.container}>
      <div className={styles.body} aria-live="polite" aria-busy={isLoading}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.skeletonCard}>
              <Skeleton variant="rect" height={12} width="30%" />
              <Skeleton variant="rect" height={4} />
              <Skeleton variant="rect" height={24} width="80%" />
              <div className={styles.skeletonPills}>
                <Skeleton variant="rect" height={36} width={100} />
                <Skeleton variant="rect" height={36} width={120} />
                <Skeleton variant="rect" height={36} width={90} />
              </div>
              <Skeleton variant="rect" height={40} />
            </div>
            <p className={styles.loadingText}>Generating personalized questions...</p>
          </div>
        ) : questions.length === 0 ? (
          <div className={styles.emptyContainer}>
            <p className={styles.emptyMessage}>
              No questions were generated. You can continue to the plan directly.
            </p>
            <Button variant="primary" size="md" onClick={handleSkip}>
              Continue to plan
            </Button>
          </div>
        ) : showReview ? (
          <ReviewScreen
            questions={questions}
            answers={questionAnswers}
            onEdit={handleEditFromReview}
          />
        ) : currentQuestion ? (
          <SingleQuestion
            key={currentQuestion.id}
            question={currentQuestion}
            answer={questionAnswers[currentQuestion.id] ?? ''}
            onAnswer={handleAnswer}
            index={currentQuestionIndex}
            total={questions.length}
          />
        ) : null}
      </div>

      {/* ---- Footer -------------------------------------------------------- */}
      {!isLoading && questions.length > 0 && (
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.skipButton}
            onClick={handleSkip}
            disabled={isSaving}
          >
            Skip all
          </button>

          <div className={styles.footerActions}>
            {(currentQuestionIndex > 0 || showReview) && (
              <button
                type="button"
                className={styles.backButton}
                onClick={handleBack}
                disabled={isSaving}
              >
                &larr; Previous
              </button>
            )}

            {showReview ? (
              <Button
                variant="primary"
                size="md"
                onClick={handleFinish}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Confirm and continue →'}
              </Button>
            ) : (
              <Button
                variant="primary"
                size="md"
                onClick={handleNext}
              >
                {isLastQuestion ? 'Review answers →' : 'Next →'}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
